// Typed client for /api/v1, shared by web and mobile. Each method maps 1:1 to
// a route in apps/api/src/routes/v1.ts and returns the shared DTOs.

import type {
  ApiErrorBody,
  ApiErrorCode,
  DashboardStats,
  DropDetail,
  DropPage,
  EmailAccountPage,
  IncomingView,
  OrderDetail,
  OrderPage,
} from './types'
import type { Retailer } from './enums'
import type { AccountListQuery, DashboardQuery, DropListQuery, IncomingQuery, OrderListQuery } from './validators'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ApiErrorCode,
    message: string,
    readonly requestId?: string,
    readonly issues?: ApiErrorBody['error']['issues'],
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export interface ApiClientOptions {
  /** Origin of the API, '' for same-origin (web). */
  baseUrl?: string
  fetch?: typeof fetch
  /** Extra headers per request, e.g. the mobile session cookie. */
  headers?: () => Record<string, string> | Promise<Record<string, string>>
}

type Query = Record<string, string | number | boolean | undefined | null>

const qs = (query?: object) => {
  const q = query as Query | undefined
  if (!q) return ''
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== null && v !== '') p.set(k, String(v))
  const s = p.toString()
  return s ? `?${s}` : ''
}

export function createApiClient({ baseUrl = '', fetch: f = fetch, headers }: ApiClientOptions = {}) {
  async function get<T>(path: string, query?: object, signal?: AbortSignal): Promise<T> {
    let res: Response
    try {
      res = await f(`${baseUrl}/api/v1${path}${qs(query)}`, {
        credentials: 'include',
        headers: { accept: 'application/json', ...(await headers?.()) },
        signal,
      })
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') throw err
      throw new ApiError(0, 'internal', "Can't reach Nebueye. Check your connection and try again.")
    }
    if (res.ok) return (await res.json()) as T
    const body = (await res.json().catch(() => null)) as ApiErrorBody | null
    throw new ApiError(
      res.status,
      body?.error.code ?? (res.status === 401 ? 'unauthenticated' : 'internal'),
      body?.error.message ?? 'Something went wrong. Try again in a moment.',
      body?.error.requestId,
      body?.error.issues,
    )
  }

  return {
    dashboard: (q: DashboardQuery, signal?: AbortSignal) => get<DashboardStats>('/dashboard', q, signal),
    orders: (q: OrderListQuery, signal?: AbortSignal) => get<OrderPage>('/orders', q, signal),
    order: (orderNumber: string, retailer?: Retailer, signal?: AbortSignal) =>
      get<OrderDetail>(`/orders/${encodeURIComponent(orderNumber)}`, { retailer }, signal),
    incoming: (q: IncomingQuery, signal?: AbortSignal) => get<IncomingView>('/incoming', q, signal),
    drops: (q: DropListQuery, signal?: AbortSignal) => get<DropPage>('/drops', q, signal),
    drop: (id: string, signal?: AbortSignal) => get<DropDetail>(`/drops/${encodeURIComponent(id)}`, undefined, signal),
    accounts: (q: AccountListQuery, signal?: AbortSignal) => get<EmailAccountPage>('/accounts', q, signal),
  }
}

export type ApiClient = ReturnType<typeof createApiClient>
